/**
 * Magento / Adobe Commerce integration service.
 *
 * Thin API client for the three admin UI surfaces:
 *   1. Connection wizard  — test-connection + save credentials
 *   2. Content Approvals  — list/approve/reject/bulk-approve write-back proposals
 *   3. Catalog Sync       — sync status + manual trigger + reset
 *
 * All backend routes live under /api/magento/* (backend-merged/routes/magento.js).
 * Bearer token is NEVER sent to the frontend — it is stored encrypted in
 * platform_connections and retrieved server-side when needed.
 */

import { authFetch } from '@/utils/auth-fetch';

// ── Typed errors ──────────────────────────────────────────────────────────────

export class MagentoNotConfiguredError extends Error {
  readonly status = 404;
  constructor() {
    super('Magento connection not configured for this site');
    this.name = 'MagentoNotConfiguredError';
  }
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface MagentoSyncState {
  site_id: string;
  sync_status: 'idle' | 'full_sync' | 'incremental' | 'error';
  last_full_sync_at: string | null;
  last_incremental_sync_at: string | null;
  last_sync_page: number;
  last_sync_sku: string | null;
  error_message: string | null;
  updated_at: string;
}

export interface MagentoWritebackProposal {
  id: string;
  site_id: string;
  product_sku: string;
  attribute_code: string;
  previous_value: string;
  proposed_value: string;
  writeback_status: 'pending' | 'applied' | 'failed' | 'rolled_back';
  source_hash: string;
  proposed_by: string;
  approved_by: string | null;
  created_at: string;
  applied_at: string | null;
  error_message: string | null;
}

export interface WritebackProposalsResponse {
  proposals: MagentoWritebackProposal[];
  limit: number;
  offset: number;
  total: number;
  hasMore: boolean;
}

export interface TestConnectionResult {
  ok: boolean;
  storeViews?: Array<{ code: string; name: string }>;
  error?: string;
}

// ── Connection ────────────────────────────────────────────────────────────────

export async function testMagentoConnection(params: {
  baseUrl: string;
  storeCode: string;
  bearerToken: string;
}): Promise<TestConnectionResult> {
  const response = await authFetch('/api/magento/test-connection', {
    method: 'POST',
    body: JSON.stringify(params),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Connection test failed');
  return data;
}

export async function saveMagentoConnection(
  siteId: string,
  params: { baseUrl: string; storeCode: string; bearerToken: string },
): Promise<void> {
  const response = await authFetch(`/api/magento/${siteId}/connection`, {
    method: 'PUT',
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to save connection');
  }
}

// ── Sync status ───────────────────────────────────────────────────────────────

export async function getMagentoSyncStatus(
  siteId: string,
  options: { signal?: AbortSignal } = {},
): Promise<MagentoSyncState> {
  const response = await authFetch(`/api/magento/${siteId}/sync-status`, {
    signal: options.signal,
  });
  if (!response.ok) {
    if (response.status === 404) throw new MagentoNotConfiguredError();
    const data = await response.json();
    throw new Error(data.error || 'Failed to fetch sync status');
  }
  return response.json();
}

export async function triggerMagentoSync(
  siteId: string,
  options: { full?: boolean } = {},
): Promise<{ mode: string; created: number; updated: number; errors: number }> {
  const response = await authFetch(`/api/magento/${siteId}/sync/trigger`, {
    method: 'POST',
    body: JSON.stringify(options),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Sync trigger failed');
  return data;
}

export async function resetMagentoSync(siteId: string): Promise<{ reset: boolean }> {
  const response = await authFetch(`/api/magento/${siteId}/sync/reset`, {
    method: 'POST',
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Reset failed');
  return data;
}

// ── Write-back proposals ──────────────────────────────────────────────────────

export async function getWritebackProposals(
  siteId: string,
  params: { limit?: number; offset?: number; signal?: AbortSignal } = {},
): Promise<WritebackProposalsResponse> {
  const qs = new URLSearchParams();
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.offset !== undefined) qs.set('offset', String(params.offset));
  const response = await authFetch(`/api/magento/${siteId}/writeback-proposals?${qs}`, {
    signal: params.signal,
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch proposals');
  return data;
}

export async function approveWriteback(logId: string, siteId: string): Promise<void> {
  const response = await authFetch(`/api/magento/writeback/${logId}/approve`, {
    method: 'POST',
    body: JSON.stringify({ siteId }),
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Approve failed');
  }
}

export async function rejectWriteback(logId: string, siteId: string): Promise<void> {
  const response = await authFetch(`/api/magento/writeback/${logId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ siteId }),
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Reject failed');
  }
}

export async function rollbackWriteback(logId: string, siteId: string): Promise<void> {
  const response = await authFetch(`/api/magento/writeback/${logId}/rollback`, {
    method: 'POST',
    body: JSON.stringify({ siteId }),
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Rollback failed');
  }
}
